import {
    IconArrowUpRight,
    IconExternalLink,
    IconHelpCircle,
    IconLock,
    IconReceiptRefund,
    IconRocket,
    IconTag,
    IconWallet,
    IconChartHistogram,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Text } from '@fragments';
import { HELP_CENTER_URL } from '../../../enums/helpArticles';

import './styles.scss';

// Category → leading glyph. Anything unmapped falls back to the help icon.
const CATEGORY_ICON = {
    gettingStarted: IconRocket,
    account: IconLock,
    billing: IconReceiptRefund,
    sales: IconTag,
    financial: IconWallet,
    reports: IconChartHistogram,
};

function openArticle(url) {
    window.open(url, '_blank', 'noopener');
}

/**
 * HelpCard — suggested help-center articles in the "Saved Actions" grammar:
 * flush list, hairline-divided rows, a neutral rounded-square category chip
 * on the left, the title in a min-width:0 column, and a rounded inverse icon
 * button pushed to the right edge. The title is a real anchor (middle-click,
 * copy address); the button opens the same article.
 */
export default function HelpCard({ articles }) {
    const { t } = useTranslation('dashboard');

    return (
        <Card
            flat
            noBodyPadding
            title={t('home.help.title')}
            subtitle={t('home.help.subtitle')}
            icon={<IconHelpCircle />}
            footer={
                <Button
                    variant="text"
                    color="inverse"
                    size="small"
                    fullWidth
                    endIcon={<IconExternalLink />}
                    onClick={() => openArticle(HELP_CENTER_URL)}
                >
                    {t('home.help.openCenter')}
                </Button>
            }
        >
            <ul className="help-card__list">
                {articles.map(a => {
                    const Icon = CATEGORY_ICON[a.category] || IconHelpCircle;
                    return (
                        <li key={a.path} className="help-card__row">
                            <span className="help-card__chip" aria-hidden="true">
                                <Icon />
                            </span>

                            <a
                                className="help-card__info"
                                href={a.url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Text.label contrast={9} maxLines={2}>{t(a.title)}</Text.label>
                            </a>

                            <Button
                                variant="rounded"
                                color="inverse"
                                size="small"
                                icon={<IconArrowUpRight />}
                                tooltip={t('home.help.openArticle')}
                                onClick={() => openArticle(a.url)}
                            />
                        </li>
                    );
                })}
            </ul>
        </Card>
    );
}
